import { inject, Injectable } from '@angular/core';

import { RoleCode } from '../models/user/user.enums';
import { RoleColorsUtil } from '../utils/role-colors.util';
import { ROLE_OPTIONS } from '../models/user/user.constants';
import { AuthService } from '../../core/services/auth.service';

/**
 * Service for user display utilities - avatar, colors, names, etc.
 * Used across header components for consistency.
 */
@Injectable({
    providedIn: 'root'
})
export class UserDisplayService {
    private readonly authService = inject(AuthService);

    /**
     * Gets the display name for the user.
     */
    public getUserDisplayName(): string {
        return this.authService.getUserName() || 'User';
    }

    /**
     * Gets the role display text in uppercase.
     */
    public getRoleDisplayText(): string {
        const activeRole = this.authService.getActiveRole();
        return `${ROLE_OPTIONS[activeRole || RoleCode.SUPERVISOR]?.label?.toUpperCase()}`;
    }

    /**
     * Gets the first letter of the username for avatar display.
     */
    public getUserInitial(): string {
        const username = this.authService.getUserName();
        return username ? username.charAt(0).toUpperCase() : 'U';
    }

    /**
     * Gets the color for a specific role.
     */
    public getRoleColor(role: RoleCode): string {
        return RoleColorsUtil.getRoleColor(role);
    }

    /**
     * Gets the avatar color based on user role.
     */
    public getAvatarColor(): string {
        const activeRole = this.authService.getActiveRole();
        return this.getRoleColor(activeRole || RoleCode.CC_VIEWER);
    }

    /**
     * Gets the avatar style object with background color.
     */
    public getAvatarStyle(): Record<string, string> {
        return {
            'background-color': this.getAvatarColor(),
            color: 'white'
        };
    }

    /**
     * Gets the gradient style using the role color.
     */
    public getGradientStyle(): Record<string, string> {
        const roleColor = this.getAvatarColor();
        const r = parseInt(roleColor.slice(1, 3), 16);
        const g = parseInt(roleColor.slice(3, 5), 16);
        const b = parseInt(roleColor.slice(5, 7), 16);
        return {
            background: `linear-gradient(to right, transparent 0%, transparent 40%, rgba(${r},${g},${b},0.2) 100%)`
        };
    }
}
